import * as THREE from "three";
import { RigidRig } from "../rigidRig";
import { groundAt } from "../terrain";
import { GroundMesh } from "../groundMesh";
import { Cell, LevelSim, SimConstants, SimEventKind, headingOffset } from "@highmarch/domain";
import { cellToWorld } from "../scene";
import { AnimRig } from "../rig";
import { HQ_RALLY_FLAG, HQ_TROOP, HQ_YAW, ROAD_Y, cube, hqNode, lib, surfaceY } from "./common";
import { FOOT_IK, FootIk } from "./footIk";
import { unevenUnder } from "./groundFollow";

/** Half the spread of a soldier's soles (world units): the ground this far round it decides whether they are placed. */
const TROOP_FOOTPRINT = 0.06;

/** The Rangers' Camp's bowman (tier IV, M21): the shipped archer troop. */
const HQ_TROOP_ARCHER = "troop_archer";
import { RING, UnitRings } from "./blobShadow";
import { BarState, HP_BAR, HpBars } from "./hpBars";
import { UnitLook, dissolveEdge, footfallSquash, popScale } from "../unitFx";
import { reducedVfx } from "../../ui/settings";
import { WornSkins } from "../skins/wornSkins";
import { EnemyViews } from "./enemies";
import type { VFX } from "../../../../../art/vfx.js";
import { KitClock } from "../skins/kit/clock";
import { TroopSkinFx, type TroopFxState } from "../skins/kit/troopFx";

interface TroopView {
  root: THREE.Group; body: THREE.Object3D; torso: THREE.Object3D | null; rig: AnimRig; hq: boolean; lod: boolean; look: UnitLook; height: number; barY: number; ringWidth: number;
  /** The HQ model it wears (the worn troop skin's soldier, or a Rangers' Camp's bowman at tier IV). */
  model: string;
  bar: BarState; lastHp: number; prev: THREE.Vector3; cur: THREE.Vector3; phase: number; age: number; stretch: number;
  /** Seconds since death (the view lingers for the topple and the dissolve), and when the dissolve starts. */
  dead: number; dissolveAt: number;
  /** Where a soldier standing still looks (a yaw about y): the raider it strikes, else down the road the formation faces. */
  faceYaw: number;
  /** The one-shot playing (TROOP_CLIP), "" when none: a block never cuts a blow, a fidget gives way to anything. */
  act: string; actStart: number; actEnd: number;
  /** Blows swung so far (the two attack clips alternate), the view time of the last blow, and when the next fidget is due. */
  swings: number; lastBlow: number; fidgetAt: number;
  /** Seconds until a cheer starts (staggered across the block), negative when none is due. */
  cheerIn: number;
  /** View time until which the soldier stands ready (no fidget): a raider is in its reach but not at its blade. */
  readyUntil: number;
  /** The Barracks that owns this soldier (TOWERS_V2), 0 for a fortress troop. */
  owner: number;
  /** The worn troop skin's own effects (TroopKit.fx: lantern light, blow trail and glint, idle sparkle). */
  skinFx: TroopFxState;
  /** Its soles placed on uneven ground after the clip (footIk.ts); null without the HQ model. */
  ik: FootIk | null;
}

/**
 * A Barracks soldier musters at the middle of its tower's cell (the sim's spawn point), inside the guardhouse; it is
 * drawn no nearer the middle than its gate, this far out (cells, the art's gate at z −0.30 in the model), so it
 * steps out of the gate rather than through the wall.
 */
export const GATE_REACH = 0.3;
/** Where a soldier `p` (x, z) mustering at a tower on `cell` is drawn: pushed out to the gate's reach. Pure. */
export function outOfGate(p: { x: number; z: number }, cell: { x: number; y: number }, gateYaw: number, out: { x: number; z: number }): { x: number; z: number } {
  const dx = p.x - cell.x, dz = p.z - cell.y, d = Math.hypot(dx, dz);
  if (d >= GATE_REACH) { out.x = p.x; out.z = p.z; return out; }
  // inside: out along its own heading from the middle (the way it walks), or the gate's when it has not moved yet
  const ux = d > 1e-4 ? dx / d : Math.sin(gateYaw), uz = d > 1e-4 ? dz / d : Math.cos(gateYaw);
  out.x = cell.x + ux * GATE_REACH; out.z = cell.y + uz * GATE_REACH;
  return out;
}

/**
 * The defender's clips (art/hq_defender.py, 2026-09-30): idle (3.2 s loop, each soldier from a random point), fidget,
 * walk, attack / attack2 (slash and thrust, alternated), hit (a shield block), die, cheer. Both attacks land their blow
 * `impact` seconds in (hq_defender IMPACT_FRAME 10 at 30 fps). The sim strikes once every `attackIntervalTicks + 1` ticks
 * while a raider is in reach, so a soldier that struck last time starts its next swing `leadTicks` before the strike
 * is due (the troop's `attackCooldown` counts down to it) and the blade lands on the tick the damage does; a first blow
 * the view could not see coming enters its clip at the impact frame.
 */
export const TROOP_CLIP = {
  attacks: ["attack", "attack2"] as readonly string[],
  impact: 10 / 30,
  /** Ticks of countdown left when the swing starts: (leadTicks + 1) ticks ≈ `impact` seconds at 20 Hz. */
  leadTicks: 6,
  /** A blow counts as "still fighting" this many strike intervals after it (then the next swing is anticipated). */
  engagedIntervals: 1.5,
  /** Seconds between a standing soldier's fidgets (uniform in the range), and the spread of a block's cheer. */
  fidget: [7, 16] as const,
  cheerSpread: 0.45,
  /** Loop cross-fades are AnimRig's; one-shots fade the loop out under them (solo), as the idle keys the arms. */
} as const;

/**
 * Melee contact (M30 #1): how near a raider must be, centre to centre in cells, for a sword soldier to SWING at it. The
 * sim strikes anything within `engageRange` (3 cells: the formation's reach, a game rule), which played the slash at
 * raiders far down the road. A raider is held at `blockRange` (0.6) by the front rank, so 0.9 is the front rank and a
 * soldier stepping up beside it; the ranks behind stand ready and face the fight instead. Bowmen (tier IV Rangers)
 * loose at any range.
 */
export const MELEE_CONTACT = 0.9;
/** How long a soldier stays ready after a strike it could not reach (a little over the sim's strike interval). */
const READY_HOLD = 1.6;

/** True when a raider at (ex, ez) is within a sword's reach of a soldier at (x, z). Pure. */
export function inMeleeContact(x: number, z: number, ex: number, ez: number, reach = MELEE_CONTACT): boolean {
  const dx = ex - x, dz = ez - z;
  return dx * dx + dz * dz <= reach * reach;
}

/** How fast a soldier standing still turns to face its target or the road (radians a second, eased). */
const TURN_RATE = 10;

/** Troop death: the same topple-then-dissolve as the enemies (units F4), the rings fade with it. */
// toppleStart: the defender is down at ~0.73 s of its 1 s `die` (drops to a knee, then falls onto the shield side)
const DIE = { dissolve: 0.45, toppleStart: 0.75, sinkFloor: -0.03, ringFade: 0.3 } as const;
const RING_TROOP = new THREE.Color(RING.troopRim);
const _dir = new THREE.Vector3();
const _gate = { x: 0, z: 0 };

/** Base troops (HQ soldier with walk/idle/attack/hit clips; nothing when its model is not in, a plain block without the library) plus the rally flag. */
export class TroopViews {
  readonly group = new THREE.Group();
  private readonly active = new Map<number, TroopView>();
  private readonly pool: TroopView[] = [];
  private readonly dying: TroopView[] = [];
  private readonly flag = new THREE.Group();
  private readonly flagRig: AnimRig;
  private readonly rings = new UnitRings(16);
  private readonly bars = new HpBars(16);
  private flagCell = new Cell(-1, -1);
  private time = 0;
  /** The reduced-effects setting, read once per sim tick (its media query allocates; not every frame). */
  private reduced = false;
  /** The skin sprites' gate (KitClock.gates().particles), read with `reduced`. */
  private particles = true;
  /** The level's VFX module (a troop skin's trails and glints), set by the level scene. */
  fx: (() => VFX | null) | null = null;

  constructor(private readonly sim: LevelSim) {
    const L = lib();
    if (L && L.hq(HQ_RALLY_FLAG)) {
      const model = L.clone(`hq:${HQ_RALLY_FLAG}`);
      model.rotation.y = HQ_YAW;
      this.flag.add(model);
      this.flagRig = new AnimRig(model, L.clipsOf(`hq:${HQ_RALLY_FLAG}`));
      this.flagRig.playLoop("idle");
    } else {
      const pole = cube(new THREE.Vector3(0, 0.45, 0), 0.05, 0.9, 0.05, 0xe8e0d0);
      const cloth = cube(new THREE.Vector3(0.18, 0.75, 0), 0.34, 0.22, 0.03, 0xe0a83c);
      this.flag.add(pole, cloth);
      this.flagRig = new AnimRig(this.flag, []);
    }
    this.group.add(this.flag, this.rings.mesh, this.bars.mesh);
  }

  private readonly pathDots = new THREE.Group();
  private pathKey = "";

  /** Leaving the level: frees the troop clones' bone textures and material clones, the flag's, and the instancing. */
  /** Where a troop is drawn this frame (world), or null when it has no view (the pet's Rally Bark rings ride on it). */
  drawnAt(id: number): THREE.Vector3 | null { return this.active.get(id)?.root.position ?? null; }

  dispose(): void {
    for (const v of [...this.active.values(), ...this.dying, ...this.pool]) { RigidRig.dispose(v.body); v.look.dispose(); }
    RigidRig.dispose(this.flag);
    this.active.clear(); this.dying.length = 0; this.pool.length = 0;
    this.rings.dispose(); this.bars.dispose();
    this.group.clear();
  }

  onTick(): void {
    const rally = this.sim.troops.rally;
    if (!rally.equals(this.flagCell)) { this.flagCell = rally; this.flag.position.copy(cellToWorld(rally, groundAt(rally.x, rally.y))); }
    this.flag.visible = !rally.equals(this.sim.grid.base);
    // Troop paths (P1.4.5): faint dots along the route from the base to the flag, rebuilt only when it changes.
    const route = this.flag.visible ? this.sim.troops.pathFrom(this.sim.grid.base) : [];
    const key = route.map((c) => `${c.x},${c.y}`).join(";");
    if (key !== this.pathKey) {
      this.pathKey = key;
      this.pathDots.clear();
      for (const c of route) { const d = cube(cellToWorld(c, groundAt(c.x, c.y) + 0.03), 0.16, 0.02, 0.16, 0x9cc4ff); this.pathDots.add(d); }
      if (this.pathDots.parent === null) this.group.add(this.pathDots);
    }
    for (const v of this.active.values()) v.prev.copy(v.cur);
    const reduced = this.reduced = reducedVfx();
    this.particles = KitClock.gates(reduced).particles;
    // fortress troops and Barracks soldiers alike (TOWERS_V2: a soldier is a troop with `ownerTowerId` set)
    for (const t of this.sim.troops.all) {
      if (!t.alive) continue;
      let v = this.active.get(t.id);
      // Tier IV (M21): a Rangers' Camp's soldiers are bowmen; a squad that just ascended changes coats at once.
      const want = this.troopModelFor(t.ownerTowerId);
      if (v && v.model !== want) { this.park(v); this.active.delete(t.id); v = undefined; }
      if (!v) { v = this.acquire(want); this.active.set(t.id, v); v.owner = t.ownerTowerId; v.prev.copy(v.cur.set(t.x.toNumber(), 0, t.y.toNumber())); v.lastHp = t.hp.toNumber(); }
      else v.cur.set(t.x.toNumber(), 0, t.y.toNumber());
      // troops have no damage event: a drop in hp since the last tick is the hit (flash + stretch)
      const hp = t.hp.toNumber();
      if (hp < v.lastHp - 1e-6) { v.look.flash(reduced ? 0.5 : 1); v.stretch = 0.06; }
      v.lastHp = hp;
      v.bar.set(hp / Math.max(1e-6, t.maxHp.toNumber()));
    }
    // Standing soldiers face the way the formation holds the road (sim/troops.ts formationSlot)...
    const [fx, fy] = headingOffset(this.sim.troops.facing);
    const roadYaw = Math.atan2(fx, fy);
    for (const v of this.active.values()) v.faceYaw = roadYaw;
    // ...a Barracks' rank faces the way its own post's formation holds the road
    for (const q of this.sim.squads) {
      if (!q.post) continue;
      const [sx, sy] = headingOffset(this.sim.troops.facingAt(q.post));
      const yaw = Math.atan2(sx, sy);
      for (const m of q.members) { const v = this.active.get(m.id); if (v) v.faceYaw = yaw; }
    }
    // ...or the raider they strike.
    const interval = (this.sim.balance.economy.troops.attackIntervalTicks + 1) / SimConstants.TicksPerSecond;
    for (const ev of this.sim.events.all()) {
      if (ev.kind === SimEventKind.StageCleared || ev.kind === SimEventKind.LevelWon) {
        for (const v of this.active.values()) v.cheerIn = Math.random() * TROOP_CLIP.cheerSpread;
        continue;
      }
      if (ev.kind === SimEventKind.EnemyBlocked) {                    // a raider's blow on the soldier holding it
        const v = this.active.get(ev.b);
        if (v && v.hq && (v.act === "" || v.act === "fidget" || v.act === "hit")) this.play(v, "hit");
        continue;
      }
      if (ev.kind !== SimEventKind.TroopAttacked) continue;
      const v = this.active.get(ev.a);
      if (!v) continue;
      const e = this.sim.enemySlots.find((x) => x.alive && x.id === ev.b);
      if (e) v.faceYaw = Math.atan2(e.x.toNumber() - v.cur.x, e.y.toNumber() - v.cur.z);
      // A sword only swings at a raider at its blade (M30 #1); one further off (the sim's engage reach) makes it stand
      // ready and face it. The blow lands now: if the view did not start this swing ahead of it, enter at its impact frame.
      const reach = this.ranged(v) || (e !== undefined && inMeleeContact(v.cur.x, v.cur.z, e.x.toNumber(), e.y.toNumber()));
      if (!reach) { v.readyUntil = this.time + READY_HOLD; continue; }
      if (v.hq && !(TROOP_CLIP.attacks.includes(v.act) && this.time - v.actStart < TROOP_CLIP.impact + 0.1)) this.swing(v, TROOP_CLIP.impact);
      v.lastBlow = this.time;
    }
    // the next blow of a soldier still fighting: start the swing so the blade lands on the strike tick
    for (const t of this.sim.troops.all) {
      if (!t.alive || t.attackCooldown !== TROOP_CLIP.leadTicks) continue;
      const v = this.active.get(t.id);
      if (!v || !v.hq || this.time - v.lastBlow > interval * TROOP_CLIP.engagedIntervals) continue;
      if (!this.ranged(v) && !this.raiderAtBlade(v)) continue;     // the raider it struck has fallen or moved off
      this.swing(v, Math.max(0, TROOP_CLIP.impact - (TROOP_CLIP.leadTicks + 1) / SimConstants.TicksPerSecond));
    }
    for (const [id, v] of this.active) {
      if (this.sim.troops.find(id) !== null) continue;
      this.active.delete(id);
      this.die(v);
    }
  }

  /** A Barracks soldier still at its tower is drawn at the gate (outOfGate), facing out of it. */
  private gate(v: TroopView): void {
    const t = this.sim.findTower(v.owner);
    if (!t) return;
    const q = this.sim.squads.find((s) => s.towerId === t.id);
    const yaw = q?.post ? Math.atan2(q.post.x - t.cell.x, q.post.y - t.cell.y) : 0;
    const p = v.root.position;
    if (Math.hypot(p.x - t.cell.x, p.z - t.cell.y) >= GATE_REACH) return;
    outOfGate(p, t.cell, yaw, _gate);
    p.x = _gate.x; p.z = _gate.z;
  }

  /** One of the two attacks, alternating, from `startAt` seconds into it. */
  private swing(v: TroopView, startAt: number): void {
    this.play(v, TROOP_CLIP.attacks[v.swings++ % TROOP_CLIP.attacks.length], startAt);
  }

  /** A soldier that looses arrows (tier IV Rangers' bowman): it attacks at any range. */
  private ranged(v: TroopView): boolean { return v.model === HQ_TROOP_ARCHER; }

  /** Whether any living ground raider is within a sword's reach of this soldier now. */
  private raiderAtBlade(v: TroopView): boolean {
    for (const e of this.sim.enemySlots) {
      if (!e.alive || e.isAir || e.burrowed) continue;
      if (inMeleeContact(v.cur.x, v.cur.z, e.x.toNumber(), e.y.toNumber())) return true;
    }
    return false;
  }

  private play(v: TroopView, clip: string, startAt = 0): void {
    const len = v.rig.playOnce(clip, false, null, { solo: true, startAt });
    if (len <= 0) return;
    v.act = clip; v.actStart = this.time - startAt; v.actEnd = this.time + len - startAt;
    if (TROOP_CLIP.attacks.includes(clip)) TroopSkinFx.blow(v.skinFx, v.actStart, TROOP_CLIP.impact);
  }

  interpolate(alpha: number, dt: number): void {
    this.time += dt;
    this.flag.rotation.y = Math.sin(this.time * 2) * 0.15;
    this.flagRig.update(dt);
    const reduced = this.reduced;
    const fx = this.fx?.() ?? null, particles = this.particles && !reduced;
    this.rings.begin();
    this.bars.begin();
    for (const v of this.active.values()) {
      v.root.position.lerpVectors(v.prev, v.cur, alpha);
      if (v.owner !== 0) this.gate(v);
      // the ground as drawn (relief and warp included), as the critters stand: groundAt is the hills alone, up to
      // ±0.09 off the mesh, which left feet sunk into the verge or hovering over a rut (T6)
      v.root.position.y = GroundMesh.drawnAt(v.root.position.x, v.root.position.z);
      const dir = _dir.subVectors(v.cur, v.prev);
      const moving = dir.lengthSq() > 1e-6;
      if (moving) v.body.rotation.y = Math.atan2(dir.x, dir.z) + (v.hq ? HQ_YAW : 0);
      else {
        // settled on its slot: turn (the short way) toward what it faces rather than keep the last step's heading
        const want = v.faceYaw + (v.hq ? HQ_YAW : 0);
        const d = Math.atan2(Math.sin(want - v.body.rotation.y), Math.cos(want - v.body.rotation.y));
        v.body.rotation.y += d * Math.min(1, dt * TURN_RATE);
      }
      v.age += dt;
      if (v.hq) {
        if (v.act !== "" && this.time >= v.actEnd) v.act = "";
        if (moving && v.act === "fidget") { v.rig.reset(); v.act = ""; }
        // a soldier walking on (to the flag, or to its slot) does not finish a swing at a raider it has left behind
        else if (moving && TROOP_CLIP.attacks.includes(v.act) && !this.ranged(v) && !this.raiderAtBlade(v)) { v.rig.reset(); v.act = ""; TroopSkinFx.cancel(v.skinFx, fx); }
        if (!moving && v.act === "" && this.time >= v.readyUntil) {
          // at rest: the block cheers a cleared stage, and now and then a soldier fidgets
          if (v.cheerIn >= 0 && (v.cheerIn -= dt) < 0) this.play(v, "cheer");
          else if (this.time >= v.fidgetAt) { this.play(v, "fidget"); v.fidgetAt = this.time + this.fidgetGap(); }
        }
        v.rig.playLoop(moving ? "walk" : "idle", 1, true);
        v.rig.update(dt);
        v.body.position.y = surfaceY();
        let k = moving && v.rig.loopClip === "walk" ? footfallSquash(v.rig.loopPhase()) : 0;
        if (v.stretch > 0) { v.stretch -= dt; k = -0.12; }
        const s = popScale(v.age);
        v.body.scale.set(s * (1 + 0.5 * k), s * (1 - k), s * (1 + 0.5 * k));
        const p = v.root.position;
        v.ik?.solve(v.body, GroundMesh.drawnAt, unevenUnder(GroundMesh.drawnAt, p.x, p.z, TROOP_FOOTPRINT) > FOOT_IK.unevenAbove, dt);
        TroopSkinFx.update(v.skinFx, this.time, dt, fx, particles, reduced, !moving && v.act === "", this.rings, v.root.position.y, Math.min(1, s));
      } else v.body.position.y = ROAD_Y + (moving ? Math.abs(Math.sin(this.time * 9 + v.phase)) * 0.1 : 0);
      v.look.update(dt, this.time, reduced);
      v.bar.update(dt);
      this.push(v, 1);
    }
    for (let i = this.dying.length - 1; i >= 0; i--) {
      const v = this.dying[i];
      v.dead += dt;
      v.rig.update(dt);
      if (v.torso && v.torso.position.y < DIE.sinkFloor) v.torso.position.y = DIE.sinkFloor;   // the baked sink, neutralised
      const d = v.dead - v.dissolveAt;
      if (d > 0) v.look.dissolve = d / DIE.dissolve;
      TroopSkinFx.update(v.skinFx, this.time, dt, fx, particles, reduced, false, this.rings, v.root.position.y, 1 - Math.max(0, d) / DIE.dissolve);
      v.look.update(dt, this.time, reduced);
      v.bar.update(dt);
      this.push(v, 1 - Math.max(0, d) / DIE.ringFade);
      if (d >= DIE.dissolve) { this.dying.splice(i, 1); this.park(v); }
    }
    this.rings.end();
    this.bars.end();
  }

  private push(v: TroopView, fade: number): void {
    const p = v.root.position;
    if (fade > 0) this.rings.pushTeam(p.x, p.y, p.z, v.ringWidth * RING.troopWidth, RING_TROOP, RING.troopRimAlpha, RING.shadowAlpha, RING.troopFill, fade);
    this.bars.push(p.x, p.y + v.barY, p.z, v.bar, HP_BAR.troop);
  }

  /** A fallen troop topples and dissolves instead of vanishing (one without an HQ model just goes). */
  private die(v: TroopView): void {
    v.bar.dying = true;
    TroopSkinFx.cancel(v.skinFx, this.fx?.() ?? null);
    if (!v.hq) { this.park(v); return; }
    v.body.scale.setScalar(1);
    v.ik?.reset();                                       // the clip's own legs for the fall
    const len = v.rig.playOnce("die", true);
    v.dead = 0;
    v.dissolveAt = len > 0 ? Math.min(DIE.toppleStart, len * 0.66) : 0;
    this.dying.push(v);
  }

  private fidgetGap(): number { const [a, b] = TROOP_CLIP.fidget; return a + Math.random() * (b - a); }

  private park(v: TroopView): void { v.root.visible = false; this.pool.push(v); }

  /** The model a troop of this owner wears: a tier-IV Rangers' Camp's bowman when it ships, else the worn soldier. */
  private troopModelFor(owner: number): string {
    const t = owner !== 0 ? this.sim.findTower(owner) : null;
    if (t !== null && t.tier >= 4) {
      const i = this.sim.ascension.branchOf[t.archetype] ?? -1;
      if (i >= 0 && this.sim.balance.branches.branches[i].id === "rangers" && lib()?.hq(HQ_TROOP_ARCHER)) return HQ_TROOP_ARCHER;
    }
    return WornSkins.troopModel();
  }

  private acquire(troop: string): TroopView {
    // Far out at gameplay zoom a new soldier uses the simplified `troop_soldier_lod1` (same nodes and clips), as the
    // enemies do (EnemyViews.lodFar); the choice is made per spawn and the pool keeps the two apart.
    const L = lib();
    // the worn troop skin's own soldier when it ships (render/skins/kit: same nodes and clips), else the stock one
    const lod = !!L && L.hq(troop) && EnemyViews.lodFar() && L.hq(`${troop}_lod1`);
    const at = this.pool.findIndex((p) => p.lod === lod && p.model === troop);
    let v = at >= 0 ? this.pool.splice(at, 1)[0] : undefined;
    if (!v) {
      const root = new THREE.Group();
      const hq = !!L && L.hq(troop);
      const model = `hq:${troop}${lod ? "_lod1" : ""}`;
      let body: THREE.Object3D;
      if (L && hq) { body = L.clone(model); body.rotation.y = HQ_YAW; WornSkins.dressTroop(body); }   // before UnitLook clones its materials
      else if (L) body = new THREE.Group();                 // its model is not in: nothing is drawn
      else body = cube(new THREE.Vector3(0, 0.3, 0), 0.3, 0.6, 0.3, 0x9cc4ff);
      const height = (hq ? L?.anchors.get(troop)?.height : undefined) ?? 0.33;
      root.add(body);
      this.group.add(root);
      v = {
        root, body, model: troop, torso: hq ? hqNode(body, troop, "body") : null, rig: new AnimRig(body, L && hq ? L.clipsOf(model) : []), hq, lod, look: new UnitLook(body, "troop", height, dissolveEdge("", "troop")),
        height, barY: hq ? height + 0.1 : 0.8, ringWidth: Math.max(0.24, height), bar: new BarState(), lastHp: 0,
        prev: new THREE.Vector3(), cur: new THREE.Vector3(), phase: Math.random() * 6.28, age: 0, stretch: 0, dead: 0, dissolveAt: 0, faceYaw: 0,
        act: "", actStart: 0, actEnd: 0, swings: 0, lastBlow: -1e9, fidgetAt: 0, cheerIn: -1, readyUntil: 0, owner: 0,
        skinFx: TroopSkinFx.state(), ik: L && hq ? FootIk.attach(body, troop) : null,
      };
      if (hq) TroopSkinFx.attach(v.skinFx, body, troop);
    }
    v.root.visible = true;
    if (at >= 0) TroopSkinFx.cancel(v.skinFx, null);
    v.rig.reset();
    v.rig.playLoop("idle", 1, true);
    v.look.reset();
    v.bar.reset();
    v.age = 0; v.stretch = 0; v.dead = 0;
    v.act = ""; v.swings = Math.random() < 0.5 ? 0 : 1; v.lastBlow = -1e9; v.cheerIn = -1; v.readyUntil = 0; v.fidgetAt = this.time + this.fidgetGap(); v.owner = 0;
    if (v.hq) v.body.scale.setScalar(0);
    return v;
  }
}
